import { MessageBar, Toolbar, ToolbarButton, ToolbarGroup } from '@fluentui/react-components';
import {
    ArchiveRegular,
    ArrowExportRegular,
    ArrowLeftRegular,
    ArrowResetRegular,
    DocumentAddRegular,
    DocumentCheckmarkRegular,
    DocumentDismissRegular,
    DocumentProhibitedRegular,
    DocumentSaveRegular,
    DocumentSyncRegular,
} from '@fluentui/react-icons';
import { getBusinessObject } from 'bpmn-js/lib/util/ModelUtil';
import { memo, useEffect, useState } from 'react';
import { triggerConfirmationDialog } from '../dialogs/ConfirmationDialog';
import {
    triggerClearMessages,
    triggerErrorMessage,
    triggerSuccessMessage,
} from '../message-bar-group/MessageBarGroup';
import { useModelerContext } from '../modeler/react/context/useModelerContext';
import { useModelerIsDirtyContext } from '../modeler/react/context/useModelerIsDirtyContext';
import type { BpmnElement } from '../modeler/types/properties-panel';
import type { ProcessSpProps } from '../modeler/types/sharepoint';
import { useToolbarStyles } from '../toolbar/toolbar-styles';
import { ToolbarLink } from '../toolbar/ToolbarLink';
import { ToolbarOverflowGroup } from '../toolbar/ToolbarOverflowGroup';
import { confirmResetProcessDefinition } from './confirm-reset-process-definition';
import {
    processDefinitionRouteApi,
    useProcessDefinitionIdParam,
    useProcessDefinitionPrevLocation,
} from './process-definition-api';
import {
    useProcessDefinitionChangeStatus,
    useProcessDefinitionDelete,
    useProcessDefinitionDeploy,
    useProcessDefinitionMutation,
    useProcessDefinitionQueryFragment,
    useProcessDefinitionQueryRefresh,
    useProcessDefinitionsReset,
} from './process-definition-query';
import {
    cleanProcessDefinitionBeforeExport,
    type IResetProcessDefinitionResult,
    resetProcess,
    resetProcessDefinition,
} from './reset-process-definition';
import { DocToolbarLink } from '../documentation/DocToolbarLink';
import type { StartWhen } from './__generated__/processDefinitionQueryMutation.graphql';
import { IncidentsDialog, triggerIncidentsDialog } from './incident-dialog.tsx';

export const ProcessDefinitionToolbar = memo(() => {
    const styles = useToolbarStyles();
    const queryFragment = useProcessDefinitionQueryFragment();
    return (
        <>
            <Toolbar className={styles.toolbar} size="small">
                <ToolbarGroup className={styles.toolbarGroup}>
                    <ToolbarLinkCancel />
                    <ToolbarButtonSave />
                </ToolbarGroup>

                <ToolbarGroupOverflowItems status={queryFragment?.status ?? ''} />

                <ToolbarGroup className={styles.toolbarGroup}>
                    <DocToolbarLink key="dl" />
                </ToolbarGroup>
            </Toolbar>
            {queryFragment?.dirtyFlag && queryFragment.status === 'ACTIVE' && (
                <MessageBar intent="warning">
                    Der aktuelle Stand der Prozessdefinition entspricht nicht der veröffentlichten
                    Version, er wurde überarbeitet und gespeichert.
                </MessageBar>
            )}
            <IncidentsDialog />
        </>
    );
});
ProcessDefinitionToolbar.displayName = 'ProcessDefinitionToolbar';

const ToolbarLinkCancel = () => {
    const prevLoc = useProcessDefinitionPrevLocation();
    return (
        <ToolbarLink
            aria-label="Abbrechen und zurück zu den Prozessdefinitionen"
            icon={<ArrowLeftRegular />}
            search={prevLoc.prevSearch}
            title="Abbrechen und zurück"
            to={prevLoc.prevPath || '/process-definitions'}
        />
    );
};

const ToolbarButtonSave = () => {
    const modeler = useModelerContext();
    const isDirty = useModelerIsDirtyContext();
    const [commit, isInFlight] = useProcessDefinitionMutation();
    const queryFragment = useProcessDefinitionQueryFragment();
    const navigate = processDefinitionRouteApi.useNavigate();
    const refresh = useProcessDefinitionQueryRefresh();
    const [disabled, setDisabled] = useState(false);

    // is used to check values to enable/disable button
    useEffect(() => {
        const getRootElement = (): any => modeler.get('canvas').getRootElement();
        const hasMissingValue = () => {
            const { id, listId, name, siteId, startWhen } = getRootElement().businessObject || {};
            return [id, listId, name, siteId, startWhen].some(
                (value) => value === undefined || value.trim() === ''
            );
        };
        const eventBus = modeler.get('eventBus');

        const eventEC: Parameters<typeof eventBus.on<'elements.changed'>>[1] = (_event, data) => {
            if (data.elements.includes(getRootElement())) {
                setDisabled(hasMissingValue());
            }
        };
        eventBus.on('elements.changed', eventEC);

        const eventID = () => {
            setDisabled(hasMissingValue());
        };
        eventBus.on('import.done', eventID);
        return () => {
            eventBus.off('elements.changed', eventEC);
            eventBus.off('import.done', eventID);
        };
    }, [modeler]);

    return (
        <ToolbarButton
            aria-label="Prozessdefinition speichern"
            disabled={isInFlight || disabled || !isDirty}
            icon={<DocumentSaveRegular />}
            onClick={() => {
                triggerClearMessages();
                const errTitle = 'Fehler beim Speichern';
                modeler
                    .saveXML()
                    .then((saveXmlResult) => {
                        if (saveXmlResult.error) {
                            throw new Error(saveXmlResult.error.message);
                        }

                        if (!saveXmlResult.xml) {
                            throw new Error('XML-Dokument fehlt');
                        }

                        const processElement = getBusinessObject(
                            modeler.get('canvas').getRootElement()
                        ) as BpmnElement<
                            ProcessSpProps & {
                                id: string;
                                name: string;
                                versionTag: string;
                            }
                        >['businessObject'];

                        commit({
                            onCompleted: (commitResult, commitErrors) => {
                                if (commitErrors && commitErrors.length > 0) {
                                    triggerErrorMessage(commitErrors, errTitle);
                                    return;
                                }

                                if (!commitResult.saveProcessDefinition) {
                                    triggerErrorMessage('Prozessdefinition fehlt', errTitle);
                                    return;
                                }

                                if (queryFragment) {
                                    refresh();
                                } else {
                                    navigate({
                                        ignoreBlocker: true,
                                        params: {
                                            id: commitResult.saveProcessDefinition.id,
                                        },
                                        replace: true,
                                        search: (prevSearch) => ({ ...prevSearch }),
                                    });
                                }

                                triggerSuccessMessage('Prozessdefinition erfolgreich gespeichert');
                            },
                            onError: (error) => {
                                triggerErrorMessage(error.message, errTitle);
                            },
                            variables: {
                                input: {
                                    definitionKey: processElement.id,
                                    definitionName: processElement.name,
                                    definitionPriority: processElement.startPriority,
                                    definitionVersion: processElement.versionTag,
                                    definitionXml: saveXmlResult.xml,
                                    list: processElement.listId,
                                    listName: processElement.listName,
                                    siteUrl: processElement.siteUrl,
                                    startWhen: processElement.startWhen as unknown as StartWhen,
                                    ...(queryFragment
                                        ? {
                                              id: queryFragment.id,
                                              status: queryFragment.status,
                                          }
                                        : {
                                              status: 'DRAFT',
                                          }),
                                },
                            },
                        });
                    })
                    .catch((error) => {
                        triggerErrorMessage(error.message, errTitle);
                    });
            }}
        >
            Speichern
        </ToolbarButton>
    );
};

const ToolbarGroupOverflowItems = (props: { status: string }) => (
    <ToolbarOverflowGroup.Group>
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemPublish.Button />}
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemDeactivate.Button />}
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemReactivate.Button />}
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemReset.Button />}
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemArchive.Button />}
        {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemTemplate.Button />}
        <itemExport.Button />
        <itemDelete.Button />
        <showIncidents.Button />
        <ToolbarOverflowGroup.Menu>
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemPublish.MenuItem />}
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && (
                <itemDeactivate.MenuItem />
            )}
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && (
                <itemReactivate.MenuItem />
            )}
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemReset.Button />}
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemArchive.MenuItem />}
            {props.status != 'TEMPLATE' && props.status != 'ARCHIVED' && <itemTemplate.MenuItem />}
            <itemExport.MenuItem />
            <itemDelete.MenuItem />
            <showIncidents.MenuItem />
        </ToolbarOverflowGroup.Menu>
    </ToolbarOverflowGroup.Group>
);

const itemPublish = new ToolbarOverflowGroup.Item(() => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const [commit, isInFlight] = useProcessDefinitionDeploy();
    const refresh = useProcessDefinitionQueryRefresh();
    const isDirty = useModelerIsDirtyContext() || !queryFragment?.dirtyFlag;

    return {
        'aria-label': 'Ausgewählte Prozessdefinition veröffentlichen',
        children: 'Veröffentlichen',
        disabled:
            !queryFragment ||
            !(queryFragment.status === 'DRAFT' || queryFragment.status === 'ACTIVE') ||
            isInFlight ||
            isDirty,
        icon: <DocumentCheckmarkRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Veröffentlichen';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.deployProcessDefinition === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition konnte nicht veröffentlicht werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition erfolgreich veröffentlicht');
                            refresh();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: queryFragment!.id },
                    });
                },
                confirmText: 'Veröffentlichen',
                content: 'Möchten Sie die geöffnete Prozessdefinition wirklich veröffentlichen?',
                title: 'Prozessdefinition veröffentlichen',
            });
        },
    };
});

const showIncidents = new ToolbarOverflowGroup.Item(() => {
    const id = useProcessDefinitionIdParam();
    return {
        'aria-label': 'Fehler Log anzeigen',
        children: 'Fehler Log',
        disabled: id === '-1', // neue/ungespeicherte Definition
        icon: <DocumentCheckmarkRegular />,
        onClick: () => triggerIncidentsDialog(id),
    };
});

const itemDeactivate = new ToolbarOverflowGroup.Item(() => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const [commit, isInFlight] = useProcessDefinitionChangeStatus();
    const refresh = useProcessDefinitionQueryRefresh();
    const isDirty = useModelerIsDirtyContext();

    return {
        'aria-label': 'Ausgewählte Prozessdefinition deaktivieren',
        children: 'Deaktivieren',
        disabled: !queryFragment || queryFragment.status !== 'ACTIVE' || isInFlight || isDirty,
        icon: <DocumentProhibitedRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Deaktivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition konnte nicht deaktiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition erfolgreich deaktiviert');
                            refresh();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: queryFragment!.id, status: 'INACTIVE' },
                    });
                },
                confirmText: 'Deaktivieren',
                content: 'Möchten Sie die geöffnete Prozessdefinition wirklich deaktivieren?',
                title: 'Prozessdefinition deaktivieren',
            });
        },
    };
});

const itemReactivate = new ToolbarOverflowGroup.Item(() => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const [commit, isInFlight] = useProcessDefinitionChangeStatus();
    const refresh = useProcessDefinitionQueryRefresh();
    const isDirty = useModelerIsDirtyContext();

    return {
        'aria-label': 'Ausgewählte Prozessdefinition reaktivieren',
        children: 'Reaktivieren',
        disabled: !queryFragment || queryFragment.status !== 'INACTIVE' || isInFlight || isDirty,
        icon: <DocumentSyncRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Reaktivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition konnte nicht reaktiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition erfolgreich reaktiviert');
                            refresh();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: queryFragment!.id, status: 'ACTIVE' },
                    });
                },
                confirmText: 'Reaktivieren',
                content: 'Möchten Sie die geöffnete Prozessdefinition wirklich reaktivieren?',
                title: 'Prozessdefinition reaktivieren',
            });
        },
    };
});

const itemReset = new ToolbarOverflowGroup.Item(() => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const [commit, isInFlight] = useProcessDefinitionsReset();
    const refresh = useProcessDefinitionQueryRefresh();
    const isDirty = useModelerIsDirtyContext();

    return {
        'aria-label': 'Ausgewählte Änderungen verwerfen',
        children: 'Verwerfen',
        disabled:
            !queryFragment ||
            queryFragment.status !== 'ACTIVE' ||
            !queryFragment.dirtyFlag ||
            isInFlight ||
            isDirty,
        icon: <ArrowResetRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Verwerfen';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.resetProcessDefinition === null) {
                                triggerErrorMessage(
                                    'Änderungen konnten nicht verworfen werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Änderungen erfolgreich verworfen');
                            refresh();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: queryFragment!.id },
                    });
                },
                confirmText: 'Verwerfen',
                content: 'Möchten Sie die Änderungen wirklich verwerfen?',
                title: 'Änderungen verwerfen',
            });
        },
    };
});

const itemArchive = new ToolbarOverflowGroup.Item(() => {
    const queryFragment = useProcessDefinitionQueryFragment();
    const [commit, isInFlight] = useProcessDefinitionChangeStatus();
    const refresh = useProcessDefinitionQueryRefresh();
    const isDirty = useModelerIsDirtyContext();

    return {
        'aria-label': 'Geöffnete Prozessdefinition archivieren',
        children: 'Archivieren',
        disabled: !queryFragment || queryFragment.status !== 'INACTIVE' || isInFlight || isDirty,
        icon: <ArchiveRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Archivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition konnte nicht archiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition erfolgreich archiviert');
                            refresh();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: queryFragment!.id, status: 'ARCHIVED' },
                    });
                },
                confirmText: 'Archivieren',
                content: 'Möchten Sie die geöffnete Prozessdefinition wirklich archivieren?',
                title: 'Prozessdefinition archivieren',
            });
        },
    };
});

const itemTemplate = new ToolbarOverflowGroup.Item(() => {
    const modelerCtx = useModelerContext();
    const queryFragment = useProcessDefinitionQueryFragment();
    // const isDirty = useModelerIsDirtyContext();
    const [commit, isInFlight] = useProcessDefinitionMutation();

    return {
        'aria-label': 'Geöffnete Prozessdefinition kopieren und als Vorlage speichern',
        children: 'Vorlage',
        disabled: /*!isDirty ||*/ isInFlight || queryFragment?.status === 'TEMPLATE', // TODO
        icon: <DocumentAddRegular />,
        onClick: () => {
            triggerClearMessages();
            const errTitle = 'Fehler beim Speichern als Vorlage';

            modelerCtx
                .saveXML()
                .then((saveXmlResult) => {
                    if (saveXmlResult.error) {
                        throw new Error(saveXmlResult.error.message);
                    }

                    if (!saveXmlResult.xml) {
                        throw new Error('XML-Dokument fehlt');
                    }

                    return new Promise<IResetProcessDefinitionResult>((resolve, reject) => {
                        confirmResetProcessDefinition((reset) => {
                            (reset
                                ? resetProcessDefinition(saveXmlResult.xml!, [
                                      'historyTimeToLive',
                                      'name',
                                      'isExecutable',
                                      'startPriority',
                                  ])
                                : resetProcess(saveXmlResult.xml!)
                            )
                                .then(resolve)
                                .catch(reject);
                        });
                    }).then((resetResult) => {
                        const bo = getBusinessObject(
                            resetResult.modeler.get('canvas').getRootElement()
                        );

                        commit({
                            onCompleted: (commitResult, commitErrors) => {
                                if (commitErrors && commitErrors.length > 0) {
                                    triggerErrorMessage(commitErrors, errTitle);
                                    return;
                                }

                                if (!commitResult.saveProcessDefinition) {
                                    triggerErrorMessage('Prozessdefinition fehlt', errTitle);
                                    return;
                                }

                                triggerSuccessMessage(
                                    'Prozessdefinition erfolgreich als Vorlage gespeichert'
                                );
                            },
                            onError: (error) => {
                                triggerErrorMessage(error.message, errTitle);
                            },
                            variables: {
                                input: {
                                    definitionKey: bo.id,
                                    definitionName: bo.name,
                                    definitionPriority: bo.startPriority,
                                    definitionVersion: bo.versionTag,
                                    definitionXml: resetResult.xml,
                                    list: '', // TODO
                                    siteUrl: bo.siteUrl,
                                    status: 'TEMPLATE',
                                    startWhen: bo.startWhen as unknown as StartWhen,
                                },
                            },
                        });
                    });
                })
                .catch((error) => {
                    triggerErrorMessage(error.message, errTitle);
                });
        },
    };
});

const itemDelete = new ToolbarOverflowGroup.Item(() => {
    const [commit, isInFlight] = useProcessDefinitionDelete();
    const queryFragment = useProcessDefinitionQueryFragment();
    const navigate = processDefinitionRouteApi.useNavigate();
    const prevLoc = useProcessDefinitionPrevLocation();

    return {
        'aria-label': 'Geöffnete Prozessdefinition löschen',
        children: 'Löschen',
        disabled: !queryFragment || queryFragment.status === 'ACTIVE' || isInFlight,
        icon: <DocumentDismissRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    if (queryFragment) {
                        const errTitle = 'Fehler beim Löschen';
                        commit({
                            onCompleted: (commitResult, commitErrors) => {
                                if (commitErrors && commitErrors.length > 0) {
                                    triggerErrorMessage(commitErrors, errTitle);
                                    return;
                                }

                                if (commitResult.deleteProcessDefinition === false) {
                                    triggerErrorMessage(
                                        'Prozessdefinition konnte nicht gelöscht werden',
                                        errTitle
                                    );
                                    return;
                                }

                                // Navigate to the list and replace the current history entry to make it no longer accessible
                                navigate({
                                    replace: true,
                                    // @ts-ignore
                                    search: prevLoc.prevSearch,
                                    to: prevLoc.prevPath || '/process-definitions',
                                }).then(() => {
                                    triggerSuccessMessage('Prozessdefinition erfolgreich gelöscht');
                                });
                            },
                            onError: (error) => {
                                triggerErrorMessage(error.message, errTitle);
                            },
                            variables: { id: queryFragment.id },
                        });
                    }
                },
                confirmText: 'Löschen',
                content: 'Möchten Sie die geöffnete Prozessdefinition wirklich löschen?',
                title: 'Prozessdefinition löschen',
            });
        },
    };
});

const itemExport = new ToolbarOverflowGroup.Item(() => {
    const modelerCtx = useModelerContext();

    return {
        'aria-label': 'Ausgewählte Prozessdefinition exportieren',
        children: 'Exportieren',
        // disabled: busy || !selectionState || selectionState.length !== 1,
        icon: <ArrowExportRegular />,
        onClick: () => {
            triggerClearMessages();
            const errTitle = 'Fehler beim Exportieren';

            modelerCtx
                .saveXML()
                .then((saveXmlResult) => {
                    if (saveXmlResult.error) {
                        throw new Error(saveXmlResult.error.message);
                    }

                    if (!saveXmlResult.xml) {
                        throw new Error('XML-Dokument fehlt');
                    }
                    cleanProcessDefinitionBeforeExport(saveXmlResult.xml, [
                        'historyTimeToLive',
                        'name',
                        'isExecutable',
                        'startPriority',
                    ]).then((xml) => {
                        // data
                        const blob = new Blob([xml], {
                            type: 'application/xml',
                        });
                        const url = URL.createObjectURL(blob);

                        const bo = getBusinessObject(modelerCtx.get('canvas').getRootElement());

                        // link
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${bo.name} Prozessdefinition.xml`.trim();
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();

                        // clean up
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    });
                })
                .catch((error) => {
                    triggerErrorMessage(error.message, errTitle);
                });
        },
    };
});
